import { IColor, IVector3 } from '../../../../../api.js';
import { ContentConstructor } from './ContentConstructor.js';
export class ContainerController {
    constructor(_container, _watcher) {
        this._container = _container;
        this._watcher = _watcher;
        this._inner_containers = new Map();
        this.custom = {
            /**
             * Creates a custom watcher of a type:
             * `string` | `number` | `boolean` | `IVector3` | `IColor`
             *
             * @param type Type of watcher: `'number'` | `'string'` | `'checkbox'` | `'vector3'` | `'color'`.
             * @param config.name Watcher label name.
             * @param config.get Function that gets a value for the watcher.
             * @param config.set Function that sets a value for the watcher.
             * @example
             * tab.custom.watch('vector3', {
             *  name: 'my_position',
             *  get: () => object.get_position(),
             *  set: (v: IVector3) => object.set_position(v)
             * });
             */
            watch: (...[type, config]) => {
                switch (type) {
                    case 'number':
                        this.watch_custom_primitive(type, config);
                        break;
                    case 'string':
                        this.watch_custom_primitive(type, config);
                        break;
                    case 'checkbox':
                        this.watch_custom_primitive(type, config);
                        break;
                    case 'vector3':
                        this.watch_custom_vector3(config);
                        break;
                    case 'color':
                        this.watch_custom_color(config);
                        break;
                }
            },
            /**
             * Creates a custom setter of a type:
             * `string` | `number` | `boolean` | `IVector3` | `IColor`
             *
             * @param type Type of setter: `'number'` | `'string'` | `'checkbox'` | `'vector3'` | `'color'`.
             * @param config.name Setter label name.
             * @param config.initial_value Setter initial value.
             * @param config.set Function that sets a value.
             *
             * @example
             * tab.custom.setter('vector3', {
             *  name: 'set_position',
             *  initial_value: new IVector3()
             *  set: (v: IVector3) => object.set_position(v)
             * });
             */
            setter: (...[type, config]) => {
                switch (type) {
                    case 'number':
                        this._content_creator.create_setter(type, config.name, config.initial_value, config.set, config);
                        break;
                    case 'string':
                        this._content_creator.create_setter(type, config.name, config.initial_value, config.set, config);
                        break;
                    case 'checkbox':
                        this._content_creator.create_setter(type, config.name, config.initial_value, config.set, config);
                        break;
                    case 'vector3':
                        this._content_creator.create_vector3_setter(config.name, config.initial_value, config.set, config);
                        break;
                    case 'color':
                        this._content_creator.create_color3_setter(config.name, config.initial_value, config.set, config);
                        break;
                }
            },
            /**
             * Creates a custom getter of a type:
             * `string` | `number` | `boolean` | `IVector3` | `IColor`
             *
             * @param type Type of getter: `'number'` | `'string'` | `'checkbox'` | `'vector3'` | `'color'`.
             * @param config.name Getter label name.
             * @param config.get Function that gets a value.
             *
             * @example
             * tab.custom.setter('vector3', {
             *  name: 'get_position',
             *  set: () => object.get_position()
             * });
             */
            getter: (...[type, config]) => {
                switch (type) {
                    case 'number':
                        this._content_creator.create_getter(type, config.name, config.initial_value, config.get, config);
                        break;
                    case 'string':
                        this._content_creator.create_getter(type, config.name, config.initial_value, config.get, config);
                        break;
                    case 'checkbox':
                        this._content_creator.create_getter(type, config.name, config.initial_value, config.get, config);
                        break;
                    case 'vector3':
                        this._content_creator.create_vector3_setter(config.name, config.initial_value, config.get, config);
                        break;
                    case 'color':
                        this._content_creator.create_color3_setter(config.name, config.initial_value, config.get, config);
                        break;
                }
            },
            /**
             * Creates a custom select menu:
             *
             * @param label Menu label.
             * @param ootions Array of options.
             * @param config.get Function that gets a value.
             * @param config.set Function that sets a value.
             *
             * @example
             * tab.custom.select(
             *  'my_menu',
             *  ['option-1', 'option-2', 'option-3'],
             *  {
             *    get: () => object.get_value()
             *    set: (v) => object.set_value(v)
             *  }
             * );
             */
            select: (label, options, config) => {
                const { get, set } = config;
                const { handler } = this._content_creator.create_select_menu(label, options, get(), { on_change: set !== null && set !== void 0 ? set : undefined });
                this.add_watcher(get, handler.set);
            }
        };
        this._content_creator = new ContentConstructor(_container, _watcher);
    }
    clear() {
        this._watcher.remove(this);
        this._inner_containers.clear();
        this._container.innerHTML = '';
    }
    /**
     * Creates a simple label text.
     *
     * @example
     * tab.label('My Options:');
     */
    label(label) {
        this._content_creator.create_label(label);
    }
    /**
     * Creates a button with a `on_click` callback.
     *
     * @example
     * tab.button('Click Me', { on_click: () => ... });
     */
    button(...args) {
        this._content_creator.create_button(...args);
    }
    /**
     * Creates a new container to encapsulate some elements.
     *
     * @example
     * const container = tab.container('My Parameters');
     * constainer.button(...)
     */
    container(...args) {
        const container = this._content_creator.create_container(...args);
        this._inner_containers.set(args[0], container);
        return container;
    }
    /**
     * Gets existing or creates a new container to encapsulate some elements.
     *
     * @example
     * const container = tab.get_container('My Parameters');
     * constainer.button(...)
     */
    get_container(...args) {
        var _a;
        return (_a = this._inner_containers.get(args[0])) !== null && _a !== void 0 ? _a : this.container(...args);
    }
    watch(target, paremeter, config = {}) {
        if (this.is_of_type(target, paremeter, 'number')) {
            return this.watch_primitive('number', target, paremeter, config);
        }
        if (this.is_of_type(target, paremeter, 'string')) {
            return this.watch_primitive('string', target, paremeter, config);
        }
        if (this.is_of_type(target, paremeter, 'checkbox')) {
            return this.watch_primitive('checkbox', target, paremeter, config);
        }
        if (this.is_of_type(target, paremeter, 'vector3')) {
            return this.watch_vector3(target, paremeter, config);
        }
        if (this.is_of_type(target, paremeter, 'color')) {
            return this.watch_color3(target, paremeter, config);
        }
    }
    setter(target, paremeter, initial_value, config = {}) {
        var _a;
        const label = (_a = config.name) !== null && _a !== void 0 ? _a : paremeter;
        // Some unavoidable casting.
        if (typeof initial_value === 'number') {
            this._content_creator.create_setter('number', label, initial_value, (v) => target[paremeter](v), config);
            return;
        }
        if (typeof initial_value === 'string') {
            this._content_creator.create_setter('string', label, initial_value, (v) => target[paremeter](v), config);
            return;
        }
        if (typeof initial_value === 'boolean') {
            this._content_creator.create_setter('checkbox', label, initial_value, (v) => target[paremeter](v), config);
            return;
        }
        if (typeof initial_value === 'object' && IVector3.is(initial_value)) {
            this._content_creator.create_vector3_setter(label, initial_value, (v) => target[paremeter](v), config);
            return;
        }
        if (typeof initial_value === 'object' && IColor.is(initial_value)) {
            this._content_creator.create_color3_setter(label, initial_value, (v) => target[paremeter](v), config);
        }
    }
    getter(target, paremeter, config) {
        var _a;
        const label = (_a = config.name) !== null && _a !== void 0 ? _a : paremeter;
        const getter = () => target[paremeter]();
        if (this.is_getter_of_type(getter, 'number')) {
            this._content_creator.create_getter('number', label, getter(), getter, config);
            return;
        }
        if (this.is_getter_of_type(getter, 'string')) {
            this._content_creator.create_getter('string', label, getter(), getter, config);
            return;
        }
        if (this.is_getter_of_type(getter, 'checkbox')) {
            this._content_creator.create_getter('checkbox', label, getter(), getter, config);
            return;
        }
        if (this.is_getter_of_type(getter, 'vector3')) {
            this._content_creator.create_vector3_getter(label, getter(), getter, config);
            return;
        }
        if (this.is_getter_of_type(getter, 'color')) {
            this._content_creator.create_color3_getter(label, getter(), getter, config);
        }
    }
    watch_vector3(target, paremeter, config = {}) {
        const { name, is_readonly } = config;
        const get = () => target[paremeter];
        const { set } = this._content_creator.create_vector3(name !== null && name !== void 0 ? name : paremeter, get(), Object.assign({ on_change: is_readonly
                ? undefined
                : (value) => {
                    target[paremeter] = value;
                } }, config)).handler;
        this.add_watcher(get, set);
    }
    watch_color3(target, paremeter, config = {}) {
        const { name, is_readonly } = config;
        const get = () => target[paremeter];
        const { set } = this._content_creator.create_color3(name !== null && name !== void 0 ? name : paremeter, get(), Object.assign({ on_change: is_readonly
                ? undefined
                : (value) => {
                    target[paremeter] = value;
                } }, config)).handler;
        this.add_watcher(get, set);
    }
    watch_primitive(type, target, paremeter, config = {}) {
        const { name, is_readonly } = config;
        const get = () => target[paremeter];
        const { set } = this._content_creator.create_input(type, name !== null && name !== void 0 ? name : paremeter, get(), {
            on_change: is_readonly
                ? undefined
                : (value) => {
                    target[paremeter] = value;
                }
        }).handler;
        this.add_watcher(get, set);
    }
    is_of_type(target, paremeter, type) {
        switch (type) {
            case 'number':
                return typeof target[paremeter] === 'number';
            case 'string':
                return typeof target[paremeter] === 'string';
            case 'checkbox':
                return typeof target[paremeter] === 'boolean';
            case 'vector3':
                return typeof target[paremeter] === 'object' && IVector3.is(target[paremeter]);
            case 'color':
                return typeof target[paremeter] === 'object' && IColor.is(target[paremeter]);
            default:
                return false;
        }
    }
    is_getter_of_type(getter, type) {
        const result = getter();
        switch (type) {
            case 'number':
                return typeof result === 'number';
            case 'string':
                return typeof result === 'string';
            case 'checkbox':
                return typeof result === 'boolean';
            case 'vector3': {
                return typeof result === 'object' && IVector3.is(result);
            }
            case 'color':
                return typeof result === 'object' && IColor.is(result);
            default:
                return false;
        }
    }
    watch_custom_primitive(type, config) {
        if ('is_nullable' in config) {
            const { name, get, set } = config;
            const { set: element_set } = this._content_creator.create_input(type, name, get(), { on_change: set !== null && set !== void 0 ? set : undefined, is_nullable: true })
                .handler;
            this.add_watcher(get, element_set);
        }
        else {
            const { name, get, set } = config;
            const { set: element_set } = this._content_creator.create_input(type, name, get(), { on_change: set !== null && set !== void 0 ? set : undefined })
                .handler;
            this.add_watcher(get, element_set);
        }
    }
    watch_custom_vector3(config) {
        if ('is_nullable' in config) {
            const { name, get, set, is_collapsed } = config;
            const { set: element_set } = this._content_creator.create_vector3(name, get(), { on_change: set !== null && set !== void 0 ? set : undefined, is_collapsed: is_collapsed, is_nullable: true }).handler;
            this.add_watcher(get, element_set);
        }
        else {
            const { name, get, set, is_collapsed } = config;
            const { set: element_set } = this._content_creator.create_vector3(name, get(), { on_change: set !== null && set !== void 0 ? set : undefined, is_collapsed: is_collapsed }).handler;
            this.add_watcher(get, element_set);
        }
    }
    watch_custom_color(config) {
        if ('is_nullable' in config) {
            const { name, get, set, is_collapsed, has_color_picker } = config;
            const { set: element_set } = this._content_creator.create_color3(name, get(), { on_change: set !== null && set !== void 0 ? set : undefined, is_collapsed: is_collapsed, has_color_picker: has_color_picker, is_nullable: true }).handler;
            this.add_watcher(get, element_set);
        }
        else {
            const { name, get, set, is_collapsed, has_color_picker } = config;
            const { set: element_set } = this._content_creator.create_color3(name, get(), { on_change: set !== null && set !== void 0 ? set : undefined, is_collapsed: is_collapsed, has_color_picker: has_color_picker }).handler;
            this.add_watcher(get, element_set);
        }
    }
    add_watcher(instance_get, element_set) {
        this._watcher.add(this, () => element_set(instance_get(), false));
    }
}
//# sourceMappingURL=ContainerController.js.map